import {
    ApiMenuCartItem,
    CartItemWithUUID,
    MRONativeLocalCart,
    NativeLocalCart,
    ReviewedCartApiResponse,
} from "@swiggy-private/cart-types";
// utils
import { isStealDealItem } from "./CartItemRenderer";
import { isEmpty } from "@swiggy-private/js-utils";
import { getMROChildCartRestaurantId, isEmptyMROCart } from "./MROFoodCart";

/**
 * Checks if cart contains only steal deal items (no regular items)
 * If cart is empty OR contains only steal deal items, returns true
 * If cart contains any non-steal deal items, returns false
 * @param cartItems - Array of cart items (ApiMenuCartItem or CartItemWithUUID)
 * @param mealItems - Array of meal items (typically empty array)
 * @returns boolean - true if cart should be considered "empty" for steal deal logic
 */
export const isEmptyCartWithStealDealCheck = (
    params: NativeLocalCart | MRONativeLocalCart,
    isMROCart: boolean,
): boolean => {
    // BAU food cart
    const cartItems: ApiMenuCartItem[] = params.cartItems || [];
    const mealItems: ReviewedCartApiResponse["mealItems"] = params.mealItems || [];

    // MRO food cart
    if (isMROCart) {
        if (isEmptyMROCart(params as MRONativeLocalCart)) {
            return true;
        }

        // find carts with only steal deal items or empty
        let invalidChildsList: string[] = params.child_carts.map(
            (childCart: MRONativeLocalCart): string | undefined => {
                const cartItems: ApiMenuCartItem[] = childCart.cartItems || [];
                const mealItems: ReviewedCartApiResponse["mealItems"] = childCart.mealItems || [];
                const restaurantId = getMROChildCartRestaurantId(childCart);

                // if cart is empty, return restaurant id for invalid child cart list
                if (isEmpty(cartItems) && isEmpty(mealItems)) {
                    return restaurantId;
                }

                // if meal items exist, return
                if (!isEmpty(mealItems)) {
                    return;
                }

                // count of non-steal deal items, if all items are steal deal items, return restaurant id for invalid child cart list
                const nonStealDealItems = cartItems.filter((item) => !isStealDealItem(item as CartItemWithUUID));
                // if all items are steal deal items, return restaurant id
                if (nonStealDealItems.length === 0) {
                    return restaurantId;
                }

                return;
            },
        );

        invalidChildsList = invalidChildsList.filter((restaurantId) => restaurantId !== undefined);
        // remove all child carts with only steal deal items or empty from the API call
        const childCarts: MRONativeLocalCart[] = params.child_carts.filter((childCart: MRONativeLocalCart) => {
            return !invalidChildsList.includes(getMROChildCartRestaurantId(childCart));
        });

        // update the child_carts in the params
        params.child_carts = [...(childCarts || [])];

        // MRO carts carry no top-level cartItems/mealItems, so the BAU checks below would always
        // report "empty". Decide emptiness from the pruned child carts and return here.
        return isEmptyMROCart(params as MRONativeLocalCart);
    }

    // If both arrays are empty, cart is truly empty
    if (isEmpty(cartItems) && isEmpty(mealItems)) {
        return true;
    }

    // If mealItems exist (not steal deal items), cart is not empty
    if (!isEmpty(mealItems)) {
        return false;
    }

    // If cartItems exist, check if they are all steal deal items
    if (!isEmpty(cartItems)) {
        const nonStealDealItems = cartItems.filter((item) => !isStealDealItem(item as CartItemWithUUID));
        return nonStealDealItems.length === 0;
    }

    return true;
};
